// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the interface font, chosen in Appearance. Applied by rewriting
// --font-sans on the document root, which every text style inherits. App.tsx
// imports it so the choice applies at launch, not only once Appearance opens.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {autorun, makeAutoObservable, runInAction} from 'mobx';

export const InterfaceFonts = {
	KONVO: 'konvo',
	SYSTEM: 'system',
	SERIF: 'serif',
} as const;
export type InterfaceFont = (typeof InterfaceFonts)[keyof typeof InterfaceFonts];

const FONT_STACKS: Record<InterfaceFont, string | null> = {
	// null = the bundled face declared in @konvo/fonts variables.css.
	konvo: null,
	system:
		"system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI Variable', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
	serif: "'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, 'Times New Roman', serif",
};

const FONT_VALUES = new Set<string>(Object.values(InterfaceFonts));

class FontPreferenceStore {
	// The device's own font by default (JP 2026-10-30): SF Pro on iPhone, as in WhatsApp.
	font: InterfaceFont = InterfaceFonts.SYSTEM;
	private _hydrated = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
		if (typeof document !== 'undefined') {
			autorun(() => {
				const stack = FONT_STACKS[this.font];
				if (stack) document.documentElement.style.setProperty('--font-sans', stack);
				else document.documentElement.style.removeProperty('--font-sans');
			});
		}
	}

	get hydrated(): boolean {
		return this._hydrated;
	}

	setFont(font: InterfaceFont): void {
		if (FONT_VALUES.has(font)) this.font = font;
	}

	private async initPersistence(): Promise<void> {
		// Version 2 drops the version 1 value once, which was Konvo for nearly everyone by default,
		// so every device moves to System. Choosing Konvo again is kept from then on.
		await makePersistent(this, 'FontPreference', ['font'], {syncAcrossTabs: true, version: 2});
		runInAction(() => {
			if (!FONT_VALUES.has(this.font)) this.font = InterfaceFonts.SYSTEM;
			this._hydrated = true;
		});
	}
}

export default new FontPreferenceStore();
